/**
 * Captures a single still frame using the browser's screen-share API.
 * Returns a PNG File. Throws if unsupported, blocked, or cancelled.
 *
 * Caller must check `isScreenCaptureSupported()` first to decide whether
 * to render the trigger UI at all.
 */
export async function captureScreenAsFile(): Promise<File> {
  if (!isScreenCaptureSupported()) {
    throw new Error('Screen capture is not supported in this browser');
  }

  // displaySurface: 'browser' nudges Chrome to default to "This tab" in the
  // picker, which is what users almost always want. Firefox ignores the hint
  // but the picker still works.
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
  });

  try {
    const [track] = stream.getVideoTracks();
    if (!track) {
      throw new Error('No video track in capture stream');
    }

    // Use an off-DOM <video> element to render one frame from the stream.
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    await video.play();
    // One requestAnimationFrame is enough to guarantee a painted frame.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );

    const width = video.videoWidth;
    const height = video.videoHeight;

    if (!width || !height) {
      throw new Error('Capture stream has no dimensions');
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to acquire 2D canvas context');
    }

    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );

    if (!blob) {
      throw new Error('Failed to encode capture as PNG');
    }

    return new File([blob], `screen-capture-${Date.now()}.png`, {
      type: 'image/png',
    });
  } finally {
    // Stop every track so the browser's "sharing" indicator disappears
    // immediately, even if anything above threw.
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }
}

export function isScreenCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  );
}

/**
 * `getDisplayMedia` rejects with a DOMException whose `name` is 'NotAllowedError'
 * both when the user dismisses the picker and when the OS or browser blocks the
 * permission. The dialog uses this to suppress the toast for cancellations
 * while still surfacing genuine errors.
 */
export function isUserCancellation(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}
